import React, { useCallback, useEffect, useState } from 'react';
import { Wrench, Trash2, Search } from 'lucide-react';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { formatCurrency } from '../../mock';
import { ReconDialog } from './ReconDialog';

const STATUSES = ['Open', 'Approved', 'In Progress', 'Waiting for Parts', 'Completed', 'Cancelled'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

export const ReconBoard = ({ canEdit, onOpenInspection }) => {
  const [d, setD] = useState({ rows: [], totals: {}, by_priority: {}, by_status: {} });
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/recon', { params: { status } }); setD(data); } catch (e) {}
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const rows = d.rows.filter((r) => !q.trim() || [r.issue, r.action, r.vehicle_desc, r.stock, r.vin].join(' ').toLowerCase().includes(q.toLowerCase()));

  const setStatusOf = async (r, s) => {
    try { await api.put(`/recon/${r.id}`, { status: s }); await load(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const del = async (r) => {
    if (!window.confirm(`Excluir item de Recon?\n\n${r.issue}\n\nLabor, peças e custos (${formatCurrency(r.totals?.total || 0)}) serão removidos. A inspeção e o veículo NÃO serão excluídos.`)) return;
    try { await api.delete(`/recon/${r.id}`); toast({ title: 'Item excluído' }); await load(); }
    catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  return (
    <div className="space-y-3" data-testid="recon-board">
      <div className="ent-ribbon" data-testid="recon-summary">
        <div><p className="ent-label">Abertos</p><p className="ent-value mt-1 t-warn" data-testid="recon-open">{d.open ?? 0}</p></div>
        <div><p className="ent-label">Concluídos</p><p className="ent-value mt-1 t-ok">{d.completed ?? 0}</p></div>
        <div><p className="ent-label">Waiting for Parts</p><p className="ent-value mt-1">{d.by_status?.['Waiting for Parts'] ?? 0}</p></div>
        <div><p className="ent-label">Critical / High</p><p className="ent-value mt-1 t-bad">{(d.by_priority?.Critical || 0)} / {(d.by_priority?.High || 0)}</p></div>
        <div><p className="ent-label">Labor</p><p className="ent-value mt-1">{formatCurrency(d.totals?.labor || 0)}</p></div>
        <div><p className="ent-label">Parts</p><p className="ent-value mt-1">{formatCurrency(d.totals?.parts || 0)}</p></div>
        <div><p className="ent-label">Outros</p><p className="ent-value mt-1">{formatCurrency(d.totals?.other || 0)}</p></div>
        <div><p className="ent-label">Total Recon</p><p className="ent-value mt-1" data-testid="recon-grand-total">{formatCurrency(d.totals?.total || 0)}</p></div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="recon-filter-all" onClick={() => setStatus('')} className={`ent-btn ${!status ? 'ent-btn-primary' : ''}`}>Todos</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`recon-filter-${s.replace(/\s/g, '-').toLowerCase()}`} onClick={() => setStatus(s)} className={`ent-btn ${status === s ? 'ent-btn-primary' : ''}`}>{s}</button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 pointer-events-none" />
          <input data-testid="recon-search" className="ent-input pl-8" style={{ width: 220 }} placeholder="Problema, veículo, stock..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="ent-panel overflow-hidden">
        <div className="ent-colhead" style={{ borderTop: '2px solid #f97316' }}>
          <Wrench className="w-3.5 h-3.5 text-orange-400" />
          <span className="ent-coltitle uppercase tracking-wide">Recon</span>
          <span className="ent-pill" data-testid="recon-count">{rows.length}</span>
        </div>
        <div className="overflow-x-auto">
          {rows.length === 0 ? <p className="ent-empty">Nenhum item de Recon</p> : (
            <table className="ent-table">
              <thead>
                <tr>
                  <th>Veículo</th><th>Problema / Ação</th>
                  <th className="hidden xl:table-cell">Origem</th>
                  <th>Prioridade</th>
                  <th className="text-right hidden lg:table-cell">Labor</th>
                  <th className="text-right hidden lg:table-cell">Parts</th>
                  <th className="text-right">Total</th>
                  <th>Status</th><th className="text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`recon-row-${r.id}`}>
                    <td>
                      <p className="font-medium">{r.vehicle_desc || '—'}</p>
                      <p className="mono text-[11px] t-label">#{r.stock} · VIN {r.vin || '—'}</p>
                    </td>
                    <td>
                      <p className="font-medium">{r.issue}</p>
                      <p className="text-[12px] text-primary">{r.action || '—'}</p>
                    </td>
                    <td className="hidden xl:table-cell text-[11px] t-label">
                      {r.source_item_label ? (
                        <button onClick={() => onOpenInspection && onOpenInspection(r.inspection_id)} className="hover:text-primary underline-offset-2 hover:underline text-left">
                          Inspeção · {r.source_item_label}
                        </button>
                      ) : 'Manual'}
                    </td>
                    <td><span className={`ent-tag ${r.priority === 'Critical' ? 't-bad' : r.priority === 'High' ? 't-warn' : ''}`}>{r.priority}</span></td>
                    <td className="num text-right hidden lg:table-cell">{formatCurrency(r.totals?.labor || 0)}</td>
                    <td className="num text-right hidden lg:table-cell">{formatCurrency(r.totals?.parts || 0)}</td>
                    <td className="num text-right font-semibold">{formatCurrency(r.totals?.total || 0)}</td>
                    <td>
                      {canEdit ? (
                        <select data-testid={`recon-status-${r.id}`} className="ent-input" style={{ width: 150 }} value={r.status} onChange={(e) => setStatusOf(r, e.target.value)}>
                          {STATUSES.map((s) => <option key={s} className="bg-popover">{s}</option>)}
                        </select>
                      ) : <span className="ent-status"><span className="ent-dot" />{r.status}</span>}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {canEdit && <button data-testid={`recon-board-edit-${r.id}`} onClick={() => setEdit(r)} className="ent-btn ent-btn-sm">Editar</button>}
                        {canEdit && <button data-testid={`recon-board-del-${r.id}`} onClick={() => del(r)} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ReconDialog open={!!edit} onClose={() => setEdit(null)} existing={edit} inspectionId={edit?.inspection_id} onSaved={load} />
    </div>
  );
};

export default ReconBoard;
